// Host-facing HTTP implementation of EntitlementApi against /v1/entitlements
// (POST list|quantity|consume|ledger, gameId in the body). Firebase-free: auth +
// base URL come from the injected BackendHttpDeps, so any host (PlaygroundHost,
// Telegram/Steam shells) can construct it.
// Do NOT import from barrel exports (entitlements/index.ts).

import {
  EntitlementApi,
  Entitlement,
  LedgerEntry,
  EntitlementQueryOptions,
} from './EntitlementApi'
import { RundotApiError } from '../errors'
import { generateId } from '../utils/idGenerator'
import { authedBackendFetch } from '../http/backendHttpDeps'
import type { BackendHttpDeps } from '../http/backendHttpDeps'

export class HttpEntitlementApi implements EntitlementApi {
  private readonly gameId: string
  private readonly deps: BackendHttpDeps

  constructor(gameId: string, deps: BackendHttpDeps) {
    this.gameId = gameId
    this.deps = deps
  }

  private async post<T>(
    path: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    const response = await authedBackendFetch(
      this.deps,
      'POST',
      `/v1/entitlements/${path}`,
      { gameId: this.gameId, ...body },
    )

    if (!response.ok) {
      const text = await response.text()
      let code = 'unknown'
      let message = text || `Request failed (${response.status})`
      try {
        const parsed: unknown = JSON.parse(text)
        if (parsed !== null && typeof parsed === 'object') {
          const errorBody = parsed as Record<string, unknown>
          if (typeof errorBody.code === 'string') code = errorBody.code
          if (typeof errorBody.error === 'string') message = errorBody.error
        }
      } catch {
        // non-JSON body — keep raw text
      }
      throw new RundotApiError(code, message, response.status)
    }
    return response.json() as Promise<T>
  }

  async listEntitlements(options?: EntitlementQueryOptions): Promise<Entitlement[]> {
    const response = await this.post<{ entitlements: Entitlement[] }>(
      'list',
      options?.scope !== undefined ? { scope: options.scope } : {},
    )
    return response.entitlements
  }

  async getQuantity(entitlementId: string, options?: EntitlementQueryOptions): Promise<number> {
    const response = await this.post<{ quantity: number }>('quantity', {
      entitlementId,
      ...(options?.scope !== undefined ? { scope: options.scope } : {}),
    })
    return response.quantity
  }

  async consumeEntitlement(
    entitlementId: string,
    quantity: number,
    callback?: (
      entitlement: Entitlement,
      referenceId: string,
    ) => void | Promise<void>,
    reason?: string,
    referenceId?: string,
  ): Promise<Entitlement> {
    referenceId = referenceId ?? generateId()
    const response = await this.post<{ entitlement: Entitlement }>('consume', {
      entitlementId,
      quantity,
      referenceId,
      reason,
    })
    const entitlement = response.entitlement
    if (callback) {
      try {
        await callback(entitlement, referenceId)
      } catch (e) {
        console.warn(
          '[RUN] consumeEntitlement callback threw, but consume succeeded',
          e,
        )
      }
    }
    return entitlement
  }

  async getLedger(
    entitlementId?: string,
    limit?: number,
    startAfter?: number,
  ): Promise<LedgerEntry[]> {
    const response = await this.post<{ ledger: LedgerEntry[] }>('ledger', {
      entitlementId,
      limit,
      startAfter,
    })
    return response.ledger
  }
}
